/**
 * AP-35 Nachtrag 12 — **die Filterleiste des Reiters Verkauf.**
 *
 * Drei Felder (Auftraggeber 20.9.2026): Zustand, Interne Notiz, Verrechnung ab.
 * Gefiltert wird im Browser über die geladenen Zeilen — die Liste einer Position
 * ist klein, und ein Filter, der den Server fragt, verlöre beim Tippen den Fokus
 * der Zeile, die gerade gespeichert wird.
 *
 * **Leeres „Verrechnung ab" heißt sofort verrechenbar.** Deshalb zählt eine leere
 * Zeile bei `vor` mit (sie ist vor jedem Datum frei) und bei `ist`/`nach` nicht.
 */
import type { ActiveFilter } from '@/components/list/list.types'
import type { SalesLineState } from '@symphenia/shared-types'

export const VERKAUF_FILTER_KEYS = ['state', 'internalNote', 'billableFrom'] as const
export type VerkaufFilterKey = (typeof VERKAUF_FILTER_KEYS)[number]

export interface VerkaufFilter {
  state?: { values: string[]; not?: boolean }
  internalNote?: { text: string; not?: boolean }
  billableFrom?: { tag: string; art: 'is' | 'gt' | 'lt' }
}

/** Was die Zeile für den Filter hergeben muss. */
export interface FilterZeile {
  state: SalesLineState
  internalNote: string | null
  billableFrom: string | null
  children?: FilterZeile[]
}

/** Die Werte der Filterleiste als `VerkaufFilter` — ohne Wert kein Filter. */
export function alsVerkaufFilter(werte: Record<string, ActiveFilter>): VerkaufFilter {
  const f: VerkaufFilter = {}
  const zustand = werte.state
  if (zustand?.value) {
    const values = zustand.value.split(',').filter(Boolean)
    if (values.length) f.state = { values, ...(zustand.operator === 'is_not' ? { not: true } : {}) }
  }
  const notiz = werte.internalNote
  if (notiz?.value.trim()) f.internalNote = { text: notiz.value.trim(), ...(notiz.operator === 'not_contains' ? { not: true } : {}) }
  const ab = werte.billableFrom
  if (ab?.value) f.billableFrom = { tag: ab.value, art: ab.operator === 'gt' ? 'gt' : ab.operator === 'lt' ? 'lt' : 'is' }
  return f
}

/** Wie viele Felder gesetzt sind — die Zahl am Filterknopf. */
export const anzahlVerkaufFilter = (f: VerkaufFilter): number => Object.keys(f).length

export function verkaufZeilePasst(z: FilterZeile, f: VerkaufFilter): boolean {
  if (f.state) {
    const drin = f.state.values.includes(z.state)
    if (drin === Boolean(f.state.not)) return false
  }
  if (f.internalNote) {
    const drin = (z.internalNote ?? '').toLowerCase().includes(f.internalNote.text.toLowerCase())
    if (drin === Boolean(f.internalNote.not)) return false
  }
  if (f.billableFrom) {
    const { tag, art } = f.billableFrom
    const wert = z.billableFrom
    // Leer heißt sofort: frei vor jedem Datum, aber kein Datum, das „ist" oder „nach" trifft.
    if (art === 'lt') return wert === null || wert < tag
    if (wert === null) return false
    if (art === 'gt' ? !(wert > tag) : wert !== tag) return false
  }
  return true
}

/**
 * Der Baum, auf das Passende gekürzt. Eine Hauptzeile bleibt, wenn sie **oder
 * eine ihrer Unterpositionen** passt; passt sie selbst, bleiben alle Unterpositionen
 * stehen. Eine Unterposition ohne ihre Hauptzeile wäre eine Zeile ohne Anspruch.
 */
export function filtereVerkauf<T extends FilterZeile>(zeilen: readonly T[], f: VerkaufFilter): T[] {
  if (!anzahlVerkaufFilter(f)) return [...zeilen]
  const aus: T[] = []
  for (const z of zeilen) {
    const kinder = (z.children ?? []) as T[]
    if (verkaufZeilePasst(z, f)) {
      aus.push(z)
      continue
    }
    const passende = kinder.filter((k) => verkaufZeilePasst(k, f))
    if (passende.length) aus.push({ ...z, children: passende })
  }
  return aus
}
